import {
  HStack,
  IconButton,
  Spacer,
  Spinner,
  StackDivider,
} from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import { Switch } from 'components/Form/Switch';
import { DuplicateItemIcon, TrashIcon } from 'components/icons';
import React from 'react';
import { graphql, useFragment, useMutation } from 'utils/graphClient';
import { QuestionControls_deleteQuestion_Mutation } from './__generated__/QuestionControls_deleteQuestion_Mutation.graphql';
import { FormsUpdateQuestionMutationInput } from './__generated__/EditQuestionCard_updateQuestion_Mutation.graphql';
import {
  QuestionControls_duplicateQuestion_Mutation,
  QuestionControls_duplicateQuestion_Mutation$variables,
} from './__generated__/QuestionControls_duplicateQuestion_Mutation.graphql';
import {
  QuestionControls_question$data,
  QuestionControls_question$key,
} from './__generated__/QuestionControls_question.graphql';
import { FormQuestionType } from 'components/icons/QuestionIcon';

const fragment = graphql`
  fragment QuestionControls_question on FormQuestion {
    id
    text
    required
    text
    definition {
      __typename
      ... on CustomField__String {
        id
      }
      ... on CustomField__Numeric {
        minValue
        maxValue
      }
      ... on CustomField__Checkboxes {
        id
        options {
          value
          text
        }
      }
      ... on CustomField__MultipleChoice {
        id
        options {
          value
          text
        }
      }
      ... on CustomField__Date {
        id
      }
      ... on CustomField__Time {
        id
      }
      ... on CustomField__DateTime {
        id
      }
      ... on CustomField__PhoneNumber {
        id
      }
      ... on CustomField__Email {
        id
      }
      ... on CustomField__Link {
        id
      }
    }
  }
`;

const deleteMutation = graphql`
  mutation QuestionControls_deleteQuestion_Mutation($id: ID!) {
    deleteQuestion(input: { id: $id }) {
      form {
        id
        questions {
          id
        }
      }
    }
  }
`;

const duplicateMutation = graphql`
  mutation QuestionControls_duplicateQuestion_Mutation(
    $input: FormsCreateQuestionMutationInput!
  ) {
    createQuestion(input: $input) {
      form {
        ...EditForm_form
      }
    }
  }
`;

interface QuestionControlsProps {
  question: QuestionControls_question$key;
  formId: string;
  isUpdating?: boolean;
  isDisabled?: boolean;
  saveQuestion: (input: FormsUpdateQuestionMutationInput) => void;
}

export const QuestionControls = ({
  question: _question,
  formId,
  isUpdating,
  isDisabled,
  saveQuestion,
}: QuestionControlsProps) => {
  const question = useFragment(fragment, _question);
  const { id, text, required, definition } = useFragment(fragment, _question);

  const [deleteQuestion, isDeleteLoading] =
    useMutation<QuestionControls_deleteQuestion_Mutation>(deleteMutation);

  const handleSwitchChange = () => {
    switch (definition?.__typename) {
      case FormQuestionType.TEXT:
        saveQuestion({
          id,
          text,
          required: !required,
          stringDefinition: {},
        });
        break;
      case FormQuestionType.NUMERIC:
        saveQuestion({
          id,
          text,
          required: !required,
          numericDefinition: {
            minValue: definition.minValue,
            maxValue: definition.maxValue,
          },
        });
        break;
      case FormQuestionType.CHECKBOXES:
        saveQuestion({
          id,
          text,
          required: !required,
          checkboxesDefinition: {
            options: definition.options,
          },
        });
        break;
      case FormQuestionType.MULTIPLECHOICE:
        saveQuestion({
          id,
          text,
          required: !required,
          multipleChoiceDefinition: {
            options: definition.options,
          },
        });
        break;
      case FormQuestionType.DATE:
        saveQuestion({
          id,
          text,
          required: !required,
          dateDefinition: {},
        });
        break;
      case FormQuestionType.TIME:
        saveQuestion({
          id,
          text,
          required: !required,
          timeDefinition: {},
        });
        break;
      case FormQuestionType.DATETIME:
        saveQuestion({
          id,
          text,
          required: !required,
          dateTimeDefinition: {},
        });
        break;
      case FormQuestionType.PHONE:
        saveQuestion({
          id,
          text,
          required: !required,
          phoneNumberDefinition: {},
        });
        break;
      case FormQuestionType.EMAIL:
        saveQuestion({
          id,
          text,
          required: !required,
          emailDefinition: {},
        });
        break;
      case FormQuestionType.LINK:
        saveQuestion({
          id,
          text,
          required: !required,
          linkDefinition: {},
        });
        break;
      default:
        break;
    }
  };
  const [duplicateQuestion, isDuplicateLoading] =
    useMutation<QuestionControls_duplicateQuestion_Mutation>(duplicateMutation);

  return (
    <HStack divider={<StackDivider />} justify="end" pt={4} borderTop="1px">
      <HStack>
        {isUpdating && <Spinner size="xs" />}
        <Spacer />
        <HStack spacing={0.5}>
          <IconButton
            variant="ghost"
            icon={<DuplicateItemIcon />}
            aria-label={t`Duplicate question`}
            isLoading={isDuplicateLoading}
            isDisabled={isDisabled}
            onClick={() => {
              duplicateQuestion({
                variables: {
                  input: createDuplicationVariables(formId, question),
                },
              });
            }}
          />
          <IconButton
            variant="ghost"
            icon={<TrashIcon />}
            aria-label={t`Delete question`}
            isLoading={isDeleteLoading}
            isDisabled={isDisabled}
            onClick={() => {
              deleteQuestion({ variables: { id } });
            }}
          />
        </HStack>
      </HStack>
      <span>
        <Switch
          isDisabled={isDisabled}
          defaultChecked={required}
          onChange={handleSwitchChange}
        >{t`Required`}</Switch>
      </span>
    </HStack>
  );
};

function createDuplicationVariables(
  formId: string,
  question: QuestionControls_question$data,
): QuestionControls_duplicateQuestion_Mutation$variables['input'] {
  const { required, text, definition } = question;
  const variables: QuestionControls_duplicateQuestion_Mutation$variables['input'] =
    {
      formId,
      required,
      text,
    };

  switch (definition?.__typename) {
    case FormQuestionType.TEXT:
      variables.stringDefinition = {};
      break;
    case FormQuestionType.NUMERIC:
      variables.numericDefinition = {
        minValue: definition.minValue,
        maxValue: definition.maxValue,
      };
      break;
    case FormQuestionType.CHECKBOXES:
      variables.checkboxesDefinition = {
        options: definition.options,
      };
      break;
    case FormQuestionType.MULTIPLECHOICE:
      variables.multipleChoiceDefinition = {
        options: definition.options,
      };
      break;
    case FormQuestionType.DATE:
      variables.dateDefinition = {};
      break;
    case FormQuestionType.TIME:
      variables.timeDefinition = {};
      break;
    case FormQuestionType.DATETIME:
      variables.dateTimeDefinition = {};
      break;
    case FormQuestionType.PHONE:
      variables.phoneNumberDefinition = {};
      break;
    case FormQuestionType.EMAIL:
      variables.emailDefinition = {};
      break;
    case FormQuestionType.LINK:
      variables.linkDefinition = {};
      break;
  }

  return variables;
}
